import { useAction, useAtom } from '@reatom/npm-react'
import { Controller } from 'react-hook-form'
import styled from 'styled-components'

import { Button, MagnifyingGlassIcon, Typography } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { useTokenAddressSearchForm } from '@root/shared/form-control'
import { useTranslation } from '@root/shared/localization'
import { AnimateHeight } from '@root/shared/ui-kit'

import type { CheckCurrencyReatom } from '@root/features/premium/check-currency/atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: CheckCurrencyReatom
} & ClassNameProps

export const CheckCurrencyHero: React.FC<Props> = ({ model, className }) => {
  const { t } = useTranslation()

  const [requestState] = useAtom(model.checkCurrencyStateAtom)
  const [network] = useAtom(selectedNetworkAtom)
  const checkCurrencyAction = useAction(model.checkCurrencyAction)

  const { control, formState, handleSubmitInternal, queryFieldRules } =
    useTokenAddressSearchForm({
      isValidAddress: Network.validateAddress(network),
      isLoading: requestState.loading,
      onSubmit: (address) => checkCurrencyAction({ address }),
    })

  const renderError = () => {
    if (!requestState.error) {
      return undefined
    }
    if (requestState.error._tag === 'CurrencyNotFoundError') {
      return <Error>{t('premium.search.error.currency-not-found')}</Error>
    }
    return <Error>{t('common.something_went_wrong_try_again')}</Error>
  }

  return (
    <Root className={className}>
      <Form onSubmit={handleSubmitInternal}>
        <InputSearch>
          <MagnifyingGlass
            size={20}
            color={formState.isValid ? '#fff' : '#707070'}
          />
          <Controller
            name="query"
            control={control}
            rules={queryFieldRules}
            render={({ field }) => (
              <Input
                required
                placeholder={t('premium.search.title')}
                {...field}
              />
            )}
          />
        </InputSearch>

        <ButtonSearch
          busy={requestState.loading}
          disabled={!formState.isValid}
          type="submit"
          variant="soft"
          radius="rounded"
        >
          {t('common.check')}
        </ButtonSearch>
      </Form>
      <AnimateHeight>{renderError()}</AnimateHeight>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
`

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 130px;
  gap: 6px;
`

const Error = styled(Typography).attrs({
  variant: 'description-medium',
  component: 'p',
})`
  color: var(--error-9);
  text-align: center;
  margin-top: 5px;
`

const Input = styled.input`
  color: var(--text-12);

  display: flex;
  justify-content: start;
  align-items: center;
  background: transparent;
  border: none;
  line-height: normal;

  flex: 1;
  height: 100%;
  font-size: var(--typography-body-2-regular-font-size);
  font-weight: var(--typography-body-2-regular-font-weight);
  width: 100%;

  &::placeholder {
    color: var(--text-10);
    transition: color ${({ theme }) => theme.transition.default};
  }
`

const MagnifyingGlass = styled(MagnifyingGlassIcon)`
  flex-shrink: 0;
  color: var(--text-10);
`

const ButtonSearch = styled(Button)`
  --button-radius-rounded: 12px;

  background: var(--backgroundA-5);

  color: var(--text-12);

  padding-inline: 15px;
  font-size: var(--typography-body-4-bold-font-size);
  font-weight: var(--typography-body-4-bold-font-weight);
  line-height: var(--typography-body-4-bold-line-height);
  height: 40px;
  width: 100%;

  &:disabled {
    color: rgba(var(--background-12-rgb), 0.33);
  }
`

const InputSearch = styled.div`
  background: var(--appearanceA-5);

  height: 40px;
  border-radius: 12px;

  line-height: 1;
  padding: 0 12px;
  font-size: var(--typography-body-2-semi-bold-font-size);
  font-weight: var(--typography-body-2-semi-bold-font-weight);
  gap: 6px;

  justify-content: center;
  display: flex;
  align-items: center;
  justify-content: start;
`
